/**
 * PATH: packages/job-hub/src/jobs/qa/QaChecklistTable.tsx
 * FILE_ID: JOBHUB-QA-TABLE-001
 * PURPOSE: Excel/Clay-style editable checklist table for one QA area. Rows edit
 *          inline (auto-grow cells), columns drag-resize (persisted), a Product
 *          column carries the quote's material, and rows can NEST as sub-rows
 *          (parent.n numbering). Right-click gives spreadsheet row actions
 *          (insert above/below, insert sub-row, duplicate, hold, move, delete) —
 *          all BEFORE the QA is run.
 * WHY:     Operator: "make the excel structure better, allow me to add sub rows,
 *          and where is the product — missing that". Build/edit surface; the
 *          run/execute view (status + photos) stays separate.
 * FLOW: cell blur -> api.updateItem ; +row -> api.addItem(area, fields, parent?) ;
 *       insert/sub-row/duplicate -> addItem then reorderItems(insertIdAt(...)) ;
 *       sibling move -> reorderItems(moveSibling(...)) ; -row -> deleteItem.
 * RELATED:
 * - ./qaApi.ts ; ./checklistTable.ts ; ./qaChecklistRowMenu.ts
 * - ./QaChecklistRow.tsx (one row) ; ./EditableCell.tsx (one clay cell)
 * - qa/api/views/floor_area.py, instance.py (the endpoints)
 */
import { Fragment, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { QueryClientContext } from '@tanstack/react-query'
import { AlertCircle, MousePointerClick } from 'lucide-react'
import { useContextMenu, useIsMdUp, type ContextMenuItem } from '@quotech/ui'
import type { QaApi, QaAreaExecution, QaItemInstance } from './qaApi'
import { QaChecklistSectionBody } from './QaChecklistSectionBody'
import { originSections } from './qaApiHelpers'
import {
  buildRowTree,
  clampColumnWidth,
  parseColumnWidths,
  type QaColumnWidths,
  type QaRowNode,
} from './checklistTable'
import { QaInlineMenuButtons } from './qaInlineMenuButtons'
import { qaRowMenuItems } from './qaRowMenuItems'
import { QaPositionedMenu } from './qaPositionedMenu'
import { buildQaChecklistRowActions, useQaChecklistEditor } from './useQaChecklistEditor'
import { setQaItemMenuOpener } from '../tabs/job-qa/qaMenuBridge'
import { ResizableHeader } from './QaChecklistRow'
import { QaChecklistGroupRow } from '../tabs/job-qa/build/QaChecklistGroupRow'
import { QA_CHECKLIST_ACTIONS_CLASS } from '../tabs/job-qa/qaWorkspaceLayout'
import { useStickyOffset } from '../tabs/job-qa/useStickyOffset'

const COL_WIDTHS_STORAGE_KEY = 'qt.jobhub.qa.checklist.colWidths.v2'

export function QaChecklistTable({
  area,
  api,
  onMutated,
  disabled = false,
  checklistId = null,
  onRenameChecklist,
  itemSelection: suppliedSelection,
  checklistSelection,
  startRenameId,
  onRenameStarted,
  scrollClassName = 'max-h-[62vh] overflow-auto',
  onUndo,
  onRedo,
  checklistMenuItems,
  onInsertFromLibrary,
}: {
  area: QaAreaExecution
  api: QaApi
  onMutated: () => void
  disabled?: boolean
  checklistId?: string | null
  onRenameChecklist?: (checklistId: string, title: string) => void
  itemSelection?: {
    isSelected: (id: string, checklistId?: string, areaId?: string) => boolean
    onToggle: (id: string, checklistId?: string, areaId?: string) => void
  }
  checklistSelection?: { isSelected: (id: string) => boolean; onToggle: (id: string) => void }
  startRenameId?: string | null
  onRenameStarted?: () => void
  /** Desktop Build passes QA_TABLE_SCROLL_CLASS so the drag height applies. */
  scrollClassName?: string
  onUndo?: () => void
  onRedo?: () => void
  checklistMenuItems?: (checklistId: string, name: string, areaId: string) => ContextMenuItem[]
  onInsertFromLibrary?: () => void
}) {
  const itemSelection = suppliedSelection ?? { isSelected: () => false, onToggle: () => {} }
  // Every write lives in the shared editor hook, so the phone card list and
  // this table cannot drift apart. Only the table's own chrome (column widths,
  // collapse, the context menu) stays local.
  const queryClient = useContext(QueryClientContext)
  const editor = useQaChecklistEditor({ area, api, onMutated, checklistId, queryClient })
  const { items, groups, busy, error, editCell } = editor
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const { ref: theadRef, height: theadH } = useStickyOffset<HTMLTableSectionElement>()

  // --- resizable columns (persisted) --------------------------------------
  const [widths, setWidths] = useState<QaColumnWidths>(() => {
    if (typeof window === 'undefined') return parseColumnWidths(null)
    return parseColumnWidths(window.localStorage.getItem(COL_WIDTHS_STORAGE_KEY))
  })
  const persistWidths = useCallback((w: QaColumnWidths) => {
    try {
      window.localStorage.setItem(COL_WIDTHS_STORAGE_KEY, JSON.stringify(w))
    } catch {
      /* storage unavailable (private mode) — widths stay in-memory only */
    }
  }, [])
  const startResize = (col: keyof QaColumnWidths, e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const startX = e.clientX
    const startW = widths[col]
    let latest = startW
    const onMove = (ev: MouseEvent) => {
      latest = clampColumnWidth(startW + (ev.clientX - startX))
      setWidths((w) => ({ ...w, [col]: latest }))
    }
    const onUp = () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
      setWidths((w) => {
        const next = { ...w, [col]: latest }
        persistWidths(next)
        return next
      })
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
  }

  // --- right-click menu ----------------------------------------------------
  const isMdUp = useIsMdUp()
  const [hoverFine] = useState(() => (
    typeof window !== 'undefined'
    && window.matchMedia('(hover: hover) and (pointer: fine)').matches
  ))
  const showRowDelete = !disabled && isMdUp && hoverFine
  const ctx = useContextMenu()
  const [menuRow, setMenuRow] = useState<QaRowNode<QaItemInstance> | null>(null)
  const [menuChecklistId, setMenuChecklistId] = useState<string | null>(null)
  const closeMenu = () => {
    ctx.close()
    setMenuRow(null)
  }

  const selectedIds = items
    .filter((item) => itemSelection.isSelected(item.id, item.checklist_instance, area.id))
    .map((item) => item.id)
  const rowItems = (node: QaRowNode<QaItemInstance>) => qaRowMenuItems({
    editor,
    node,
    disabled,
    selectedIds,
    inspectionId: area.inspection_id ?? '',
    api,
    onMutated,
    setError: editor.setError,
    onUndo,
    onRedo,
  })

  const openRowMenu = (
    node: QaRowNode<QaItemInstance>,
    e: React.MouseEvent | { clientX: number; clientY: number },
  ) => {
    setMenuRow(node)
    ctx.openAt(e)
  }
  const rowsById = useRef(new Map<string, QaRowNode<QaItemInstance>>())
  const openRowMenuRef = useRef(openRowMenu)
  openRowMenuRef.current = openRowMenu
  const drawnRows = new Map<string, QaRowNode<QaItemInstance>>()
  for (const group of groups) {
    for (const section of originSections(group.items, { flat: !!group.checklist.template_built })) {
      for (const node of buildRowTree(section.items)) drawnRows.set(node.item.id, node)
    }
  }
  rowsById.current = drawnRows
  useEffect(() => {
    setQaItemMenuOpener((itemId, point) => {
      const node = rowsById.current.get(itemId)
      if (!node) return
      openRowMenuRef.current(node, point)
    })
    return () => setQaItemMenuOpener(null)
  }, [])

  const total = 44 + widths.item + widths.product + widths.requirement + widths.acceptance + 56

  return (
    <div className="rounded-xl border border-border bg-card overflow-hidden">
      {error && (
        <p role="alert" className="flex items-start gap-1.5 border-b border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          {error}
        </p>
      )}
      <div
        className={scrollClassName}
        style={{ ['--qa-thead-top' as string]: `${theadH}px` }}
      >
        <table className="border-collapse text-left" style={{ tableLayout: 'fixed', width: total, minWidth: '100%' }}>
          <colgroup>
            <col style={{ width: 44 }} />
            <col style={{ width: widths.item }} />
            <col style={{ width: widths.product }} />
            <col style={{ width: widths.requirement }} />
            <col style={{ width: widths.acceptance }} />
            <col style={{ width: 56 }} />
          </colgroup>
          <thead ref={theadRef} className="sticky top-0 z-10">
            <tr className="border-b border-border bg-muted text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              <th className="border-r border-border/50 px-2 py-2 text-center">#</th>
              <ResizableHeader label="Item" onResize={(e) => startResize('item', e)} disabled={disabled} />
              <ResizableHeader label="Product" onResize={(e) => startResize('product', e)} disabled={disabled} />
              <ResizableHeader label="Requirement" onResize={(e) => startResize('requirement', e)} disabled={disabled} />
              <ResizableHeader
                label="Acceptance criteria"
                onResize={(e) => startResize('acceptance', e)}
                disabled={disabled}
              />
              <th className="px-2 py-2 text-center" title="Hold point: shows as (Hold Point) in the ITP with a sign-off">
                Hold
              </th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-sm text-muted-foreground">
                  No rows yet. Add the first check below.
                </td>
              </tr>
            ) : (
              groups.map((group) => (
                <Fragment key={group.checklist.id}>
                  <QaChecklistGroupRow
                    name={group.name}
                    done={group.done}
                    total={group.total}
                    detail={(() => {
                      const names = group.items.map((item) => item.assigned_to_name).filter(Boolean)
                      return group.items.length > 0 && names.length === group.items.length && new Set(names).size === 1
                        ? names[0]
                        : undefined
                    })()}
                    collapsed={!!collapsed[group.checklist.id]}
                    onToggle={() =>
                      setCollapsed((state) => ({
                        ...state,
                        [group.checklist.id]: !state[group.checklist.id],
                      }))
                    }
                    onRename={
                      onRenameChecklist && !disabled
                        ? (title) => onRenameChecklist(group.checklist.id, title)
                        : undefined
                    }
                    selected={checklistSelection?.isSelected(group.checklist.id)}
                    onToggleSelected={checklistSelection
                      ? () => checklistSelection.onToggle(group.checklist.id)
                      : undefined}
                    startRename={startRenameId === group.checklist.id}
                    onRenameStarted={onRenameStarted}
                    stickyClass="top-[var(--qa-thead-top,0px)]"
                    checklistId={group.checklist.id}
                    areaId={area.id}
                    onLongPress={!isMdUp && checklistMenuItems
                      ? () => setMenuChecklistId(group.checklist.id)
                      : undefined}
                  />
                  {!isMdUp && menuChecklistId === group.checklist.id && checklistMenuItems ? (
                    <tr>
                      <td colSpan={6}>
                        <QaInlineMenuButtons
                          items={checklistMenuItems(group.checklist.id, group.name, area.id)}
                          onDone={() => setMenuChecklistId(null)}
                        />
                      </td>
                    </tr>
                  ) : null}
                  {!collapsed[group.checklist.id] &&
                    originSections(group.items, { flat: !!group.checklist.template_built }).map((section) => (
                      <QaChecklistSectionBody
                        key={`${group.checklist.id}-${section.key}`}
                        title={section.title}
                        origin={section.origin}
                        showHeader={section.key !== 'all'}
                        nodes={buildRowTree(section.items)}
                        disabled={disabled}
                        busy={busy}
                        onEditCell={editCell}
                        onContextMenu={openRowMenu}
                        itemSelection={itemSelection}
                        itemIds={suppliedSelection ? section.items.map((item) => item.id) : undefined}
                        checklistId={group.checklist.id}
                        onAdd={() => editor.addOriginRow(group.checklist.id, section.items, section.origin)}
                        addBusy={busy === 'insert'}
                        areaNames={area.product_names}
                        checklistNames={group.checklist.product_names}
                        actionRowId={isMdUp ? null : menuRow?.item.id ?? null}
                        actionItems={!isMdUp && menuRow ? rowItems(menuRow) : undefined}
                        onActionDone={closeMenu}
                        onMoveUp={(item) => editor.reorderSibling(item, -1)}
                        onMoveDown={(item) => editor.reorderSibling(item, 1)}
                        onInsertFromLibrary={checklistId ? onInsertFromLibrary : undefined}
                        onQuickDelete={showRowDelete
                          // Reused from: qa/useQaChecklistEditor.ts (buildQaChecklistRowActions.onDelete)
                          ? (item) => { buildQaChecklistRowActions(editor, item).onDelete() }
                          : undefined}
                      />
                    ))}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2 border-t border-border px-3 py-2">
        <div className={QA_CHECKLIST_ACTIONS_CLASS}>
          {!disabled && (
            <span className="hidden items-center gap-1 text-[11px] text-muted-foreground/70 sm:flex">
              <MousePointerClick className="h-3 w-3" aria-hidden /> Right-click, long-press, or tap · on a row for insert / sub-rows
            </span>
          )}
        </div>
        {error && (
          <span className="flex items-center gap-1.5 text-xs text-destructive">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden /> {error}
          </span>
        )}
      </div>

      {menuRow && isMdUp ? (
        <QaPositionedMenu
          x={ctx.props.position.x}
          y={ctx.props.position.y}
          items={rowItems(menuRow)}
          onClose={closeMenu}
        />
      ) : null}
    </div>
  )
}

export default QaChecklistTable
